import type { ReactNode } from 'react';
import type { PlayerRow } from '../../../services/tournament2026Queries';
import { formatPlayerTier } from '../viewUtils';

export function TextField({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}) {
  return (
    <label className="block font-data text-xs tracking-[0.14em] text-[#8B949E]">
      {label}
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        type={type}
        required
        className="mt-1 min-h-11 w-full rounded-md border border-[#27272A] bg-[#050506] px-3 py-2 text-sm normal-case tracking-normal text-[#E6EDF3] outline-none focus:border-[#3FB950]"
      />
    </label>
  );
}

export function ScorePicker({
  label,
  subtitle,
  value,
  onChange,
}: {
  label: string;
  subtitle?: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const numericValue = parseScoreValue(value);
  const selectedValue = numericValue?.toString() ?? '';
  const quickValues = getQuickScoreValues(numericValue);
  const decrementValue = numericValue ? Math.max(1, numericValue - 1) : 3;
  const incrementValue = numericValue ? numericValue + 1 : 5;

  return (
    <div className="min-w-0 font-data text-xs tracking-[0.14em] text-[#8B949E]">
      <div>{label}</div>
      {subtitle && (
        <div className="mt-0.5 truncate text-[10px] normal-case tracking-normal text-[#A1A1AA]">
          {subtitle}
        </div>
      )}
      <div className="mt-1 min-w-0 border-y border-[#27272A] bg-[#050506] py-2 sm:rounded-md sm:border sm:px-2">
        <div className="grid grid-cols-[2.75rem_minmax(0,1fr)_2.75rem] items-center gap-2">
          <button
            type="button"
            onClick={() => onChange(decrementValue.toString())}
            disabled={!numericValue || numericValue <= 1}
            aria-label={`${label} decrement`}
            className="min-h-11 rounded-md border border-[#27272A] text-lg font-bold text-[#E6EDF3] disabled:text-[#484F58]"
          >
            -
          </button>
          <select
            value={selectedValue}
            onChange={(event) => onChange(event.target.value)}
            aria-label={`${label} score`}
            className="min-h-11 min-w-0 w-full appearance-none rounded-md border border-[#3F3F46] bg-[#050506] px-2 text-center text-lg font-bold tabular-nums text-[#FAFAFA] outline-none focus:border-[#3FB950]"
          >
            <option value="">--</option>
            {Array.from({ length: 12 }, (_, index) => index + 1).map((score) => (
              <option key={score} value={score}>
                {score}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => onChange(incrementValue.toString())}
            aria-label={`${label} increment`}
            className="min-h-11 rounded-md border border-[#27272A] text-lg font-bold text-[#E6EDF3]"
          >
            +
          </button>
        </div>
        <div className="mt-2 grid grid-cols-5 gap-1">
          {quickValues.map((score) => (
            <button
              key={score}
              type="button"
              onClick={() => onChange(score.toString())}
              className={`min-h-11 rounded border px-1 py-2 text-xs font-bold tabular-nums ${
                numericValue === score
                  ? 'border-[#3FB950] bg-[#06170B] text-[#3FB950]'
                  : 'border-[#27272A] text-[#A1A1AA]'
              }`}
            >
              {score}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function parseScoreValue(value: string): number | null {
  const score = Number(value);

  return Number.isInteger(score) && score > 0 ? score : null;
}

function getQuickScoreValues(value: number | null): number[] {
  const center = value ?? 4;
  const start = Math.max(1, center - 2);

  return Array.from({ length: 5 }, (_, index) => start + index);
}

export function PlayerSelect({
  label,
  value,
  players,
  onChange,
}: {
  label: string;
  value: string;
  players: PlayerRow[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="block font-data text-xs tracking-[0.14em] text-[#8B949E]">
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 min-h-11 w-full rounded-md border border-[#27272A] bg-[#050506] px-3 py-2 text-sm normal-case tracking-normal text-[#E6EDF3] outline-none focus:border-[#3FB950]"
      >
        <option value="">Select player</option>
        {players.map((player) => (
          <option key={player.id} value={player.id}>
            {formatPlayerOptionLabel(player)}
          </option>
        ))}
      </select>
    </label>
  );
}

function formatPlayerOptionLabel(player: PlayerRow): string {
  return `${player.name} · ${player.team} · HCP ${player.current_cpi ?? '-'} · ${formatPlayerTier(player.tier)}`;
}

export function SubmitButton({
  children,
  isSaving,
  disabled = false,
}: {
  children: ReactNode;
  isSaving: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="submit"
      disabled={disabled || isSaving}
      className="min-h-11 w-full rounded-md bg-[#3FB950] px-3 py-2 font-data text-sm font-bold text-[#09090B] disabled:opacity-60"
    >
      {isSaving ? 'Saving…' : children}
    </button>
  );
}
